/**
 * Le bureau d'AquariumOS : quelles fenêtres sont ouvertes, réduites, fermées.
 *
 * Aucun DOM ici. Les trois pastilles d'une barre de titre étaient décoratives —
 * elles pilotent maintenant un vrai état, et un état de fenêtrage est
 * exactement le genre de chose qui se casse en silence : une fenêtre fermée qui
 * ne revient plus, un « tout restaurer » qui oublie l'agrandissement, une barre
 * des tâches qui liste une fenêtre déjà fermée. Tout ça se vérifie sans écran.
 */

/** Les sept fenêtres du bac, dans l'ordre de la page. */
export const DESK_WINDOWS = [
  'live',
  'log',
  'monitor',
  'lore',
  'mixer',
  'faq',
  'guestbook',
] as const;

export type DeskWindow = (typeof DESK_WINDOWS)[number];

/**
 * L'état d'une fenêtre.
 *
 * `zoomed` survit à une réduction et à une fermeture : rouvrir une fenêtre
 * qu'on avait élargie la rend telle qu'on l'avait laissée, comme dans n'importe
 * quel gestionnaire de fenêtres.
 */
export interface DeskItem {
  readonly mode: 'open' | 'minimised' | 'closed';
  readonly zoomed: boolean;
}

export type Desk = Readonly<Record<DeskWindow, DeskItem>>;

/**
 * Au démarrage, tout est ouvert, et les deux fenêtres pleine largeur du handoff
 * le sont déjà : `steve_live.exe` en haut, `livre_dor.exe` en bas.
 */
export const DESK_START: Desk = Object.freeze(
  Object.fromEntries(
    DESK_WINDOWS.map((id) => [
      id,
      { mode: 'open', zoomed: id === 'live' || id === 'guestbook' },
    ]),
  ) as Record<DeskWindow, DeskItem>,
);

/** Remplace une fenêtre, sans toucher aux six autres. */
function patch(desk: Desk, id: DeskWindow, item: Partial<DeskItem>): Desk {
  return { ...desk, [id]: { ...desk[id], ...item } };
}

/** Réduit une fenêtre à sa barre de titre. Elle reste dans la barre des tâches. */
export function minimise(desk: Desk, id: DeskWindow): Desk {
  return patch(desk, id, { mode: 'minimised' });
}

/** Ferme une fenêtre. Elle disparaît de la page, pas du menu « Fenêtres ». */
export function closeWindow(desk: Desk, id: DeskWindow): Desk {
  return patch(desk, id, { mode: 'closed' });
}

/**
 * Ramène une fenêtre au premier plan.
 *
 * Une seule fonction pour rouvrir **et** dé-réduire : la barre des tâches et le
 * menu ne veulent jamais autre chose, et deux fonctions laisseraient un chemin
 * où une fenêtre fermée redevient « réduite », c'est-à-dire invisible sans que
 * rien ne le dise.
 */
export function restore(desk: Desk, id: DeskWindow): Desk {
  return patch(desk, id, { mode: 'open' });
}

/** Réduit une fenêtre ouverte, restaure une fenêtre qui ne l'est pas. */
export function toggleWindow(desk: Desk, id: DeskWindow): Desk {
  return desk[id].mode === 'open' ? minimise(desk, id) : restore(desk, id);
}

/** Bascule la largeur. Utilisable sur une fenêtre réduite : elle se rouvre. */
export function toggleZoom(desk: Desk, id: DeskWindow): Desk {
  return patch(desk, id, { mode: 'open', zoomed: !desk[id].zoomed });
}

/** Tout remettre à l'écran, agrandissements compris tels qu'ils étaient. */
export function restoreAll(desk: Desk): Desk {
  return Object.fromEntries(
    DESK_WINDOWS.map((id) => [id, { ...desk[id], mode: 'open' as const }]),
  ) as Desk;
}

/** Tout réduire d'un coup : le geste « bureau » de n'importe quel système. */
export function minimiseAll(desk: Desk): Desk {
  return Object.fromEntries(
    DESK_WINDOWS.map((id) => [
      id,
      { ...desk[id], mode: desk[id].mode === 'closed' ? 'closed' : ('minimised' as const) },
    ]),
  ) as Desk;
}

/** Ce que la barre des tâches affiche : tout sauf ce qui est fermé. */
export function taskbarWindows(desk: Desk): readonly DeskWindow[] {
  return DESK_WINDOWS.filter((id) => desk[id].mode !== 'closed');
}

/** Combien de fenêtres sont réellement visibles — le compte de la barre. */
export function openCount(desk: Desk): number {
  return DESK_WINDOWS.filter((id) => desk[id].mode === 'open').length;
}
